'use client';

import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { Logo } from '@/components/Logo';
import { WavyBackground } from '@/components/ui/wavy-background';

// Brand-tinted wave palette — tokens resolve per theme (pastel light / saturated dark).
const WAVE_COLORS = [
  'var(--brand-wave-1)',
  'var(--brand-wave-2)',
  'var(--brand-wave-3)',
  'var(--brand-wave-4)',
  'var(--brand-wave-5)',
];

type AuthLayoutProps = {
  children: ReactNode;
};

export function AuthLayout({ children }: AuthLayoutProps) {
  const translate = useTranslations();

  return (
    <main className="min-h-screen flex flex-col md:flex-row bg-background">
      {/* Mobile-only mesh-bg header strip with brand logo overlay */}
      <section
        aria-label={translate('auth.brand.panelLabel')}
        className="relative isolate flex h-32 w-full items-center justify-center overflow-hidden bg-surface-1 md:hidden"
      >
        <div className="mesh-bg absolute inset-0 opacity-30" aria-hidden="true" />
        <Logo className="relative z-10 h-8 w-auto" />
      </section>

      {/* Form column — w-[480px] centered on desktop, full width on mobile */}
      <section className="flex w-full flex-1 items-center justify-center px-6 py-12 md:px-12">
        <div className="w-full max-w-[480px]">
          {children}
        </div>
      </section>

      {/* Brand panel with animated wavy background — desktop md+ only */}
      <aside
        aria-label={translate('auth.brand.panelLabel')}
        className="relative isolate hidden min-h-screen flex-1 overflow-hidden border-l border-outline-variant md:flex"
      >
        <WavyBackground
          colors={WAVE_COLORS}
          waveOpacity={0.35}
          backgroundFill="var(--surface-1)"
          blur={12}
          speed="slow"
          containerClassName="absolute inset-0 h-full w-full"
          className="relative z-10 flex h-full w-full flex-col items-center justify-center p-12 lg:p-16"
        >
          <div className="flex w-full max-w-[480px] flex-col items-center gap-8 text-center">
            <Logo className="h-12 w-auto" />
            <p className="type-headline-s text-on-surface-variant max-w-[28ch]">
              {translate('auth.brand.tagline')}
            </p>
          </div>
        </WavyBackground>
      </aside>
    </main>
  );
}
